import { ExclamationCircleOutlined } from '@ant-design/icons';
import { captureException } from '@sentry/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Checkbox, CheckboxProps, Form as FormAntd, Spin, Typography } from 'antd';
import axios from 'axios';
import { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { v4 } from 'uuid';
import { useAppContext } from '../../context';
import { enTermsGenerationMoment } from '../../enums/enTermsGenerationMoment';
import {
    formatPhone,
    setupFacebookPixel,
    setupGoogleTagManager,
    trackFacebookPixelEvent,
} from '../../helpers/functions';
import { usePreview } from '../../hooks/usePreview';
import { ICheckout, ICheckoutInstallment } from '../../interfaces/ICheckout';
import { ICoupon } from '../../interfaces/ICoupon';
import { IStyledProp } from '../../interfaces/IStyledProp';
import { ITransaction } from '../../interfaces/ITransaction';
import { checkoutService } from '../../services/CheckoutService';
import { socketService } from '../../services/SocketService';
import { trackService } from '../../services/TrackService';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import AlertHomologMode from '../TestAlert';
import ErrorPage from '../globals/ErrorPage';
import CardDefault from '../shared/CardDefault';
import BannerBottom from './Banners/Bottom';
import BannerRight from './Banners/Right';
import BannerTop from './Banners/Top';
import Address from './Customer/Address';
import BasicInfo from './Customer/BasicInfo';
import BasicInfoResumed from './Customer/BasicInfoResumed';
import { FinancialResponsible } from './Customer/FinancialResponsible';
import ExternalDocumentModal from './ExternalDocumentModal';
import Footer from './Footer';
import Header from './Header';
import IdempotencyModal from './IdempotencyModal';
import OrderBump from './OrderBump';
import Owner from './Owner';
import PayingModal from './PayingModal';
import Payment from './Payment';
import RefusedModal from './RefusedModal';
import TermsModal from './TermsModal';
import FormProvider from './context';
import { buildCustomer, buildFinancialResponsible, buildOCBData, buildPayments, documentMask, moneyMask, phoneMask } from './helper';

const Text = Typography.Text;

export interface IProps extends IStyledProp {
  id: string;
  salesKey?: string;
  modeAs: 'checkout' | 'transaction' | 'preview';
}

const Form = memo<IProps>(({ className, modeAs, id, salesKey }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState<ICheckout['items']>([]);
  const [isRefused, setRefused] = useState(false);
  const [cachedData, setCachedData] = useState<ICheckout>();
  const [ocbData, setOcbData] = useState<any>();
  const [isForeigner, setIsForeigner] = useState(false);
  const [isFinancialResponsibleForeigner, setIsFinancialResponsibleForeigner] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [showTerms, setShowTerms] = useState(false);
  const [enablePay, setEnablePay] = useState(false);
  const [isSaveOCB, setSaveOCB] = useState(true);
  const [refusedError, setRefusedError] = useState<Error | null>(null);
  const [shouldIgnoreIdempotency, setShouldIgnoreIdempotency] = useState(false);
  const [transaction, setTransaction] = useState<ITransaction | undefined>();
  const [canSignDocument, setCanSignDocument] = useState(true);
  const [isDocumentSigned, setDocumentSigned] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<ICoupon | undefined>();
  const [installments, setInstallments] = useState<ICheckoutInstallment[]>([]);

  const { setStyle } = useAppContext();

  const onChange: CheckboxProps['onChange'] = (e) => {
    setEnablePay(e.target.checked);
  };

  const onChangeSaveOCB: CheckboxProps['onChange'] = (e) => {
    setSaveOCB(e.target.checked);
  };

  const { prewiewData } = usePreview(id, modeAs);

  const isCached = !!window.__data && modeAs == 'checkout';
  const isOCB = !!ocbData;

  let { isLoading, data, error, isError } = useQuery({
    queryKey: [modeAs, id, salesKey],
    queryFn: async () => {
      if (modeAs == 'transaction') {
        return checkoutService.mountAsTransaction(id);
      }

      return checkoutService.mountAsCheckout(id, salesKey);
    },
    enabled: modeAs != 'preview' && !isCached,
    retry: 0,
  });

  data = modeAs == 'preview' ? prewiewData : isCached ? cachedData : data;

  const hasDocumentoToSign = data?.hasExternalDocument && data?.termsGenerationMoment == enTermsGenerationMoment.BEFORE;
  const shouldGenerateTransactionFirst = hasDocumentoToSign && !transaction?.id;

  const { mutate, isPending } = useMutation({
    mutationFn: async (body: any) => {
      if (!data) {
        return;
      }

      if (transaction?.id && hasDocumentoToSign && !isDocumentSigned) {
        setCanSignDocument(true);
        return;
      }

      const idempotencyKey = localStorage.getItem(`idempotency-${data.id}`) ?? v4();
      localStorage.setItem(`idempotency-${data.id}`, idempotencyKey);

      const payments = !shouldGenerateTransactionFirst ? await buildPayments(data.teamId, body) : [];
      const hasInvalidPayment = payments.some((payment) => !payment);

      if (hasInvalidPayment && !shouldGenerateTransactionFirst) {
        captureException(new Error('Invalid payment'), { extra: { data, body } });
        setRefused(true);
        return;
      }

      const customer = buildCustomer(body, isForeigner);
      const financialResponsible = buildFinancialResponsible(body, isFinancialResponsibleForeigner);

      const query = Array.from(searchParams.entries()).reduce(
        (acc, [key, value]) => {
          acc[key] = value;
          return acc;
        },
        {} as { [key: string]: string }
      );

      const payload = {
        ...body,
        customer,
        payments,
        teamId: data.teamId,
        id: transaction?.id ?? data.id,
        items,
        query,
        salesKey,
        isCached,
        modeAs: transaction?.id ? 'transaction' : modeAs,
        ...(!shouldIgnoreIdempotency && { idempotencyKey }),
        shouldGenerateTransactionFirst,
        coupon: appliedCoupon,
        ...(financialResponsible ? { financialResponsible } : {}),
        type: data.type,
      };

      if (!shouldGenerateTransactionFirst) {
        const eventId = v4();
        trackService.trackEvent(
          {
            eventName: 'InitiateCheckout',
            eventTime: Date.now(),
            eventSourceUrl: window.location.href,
            checkoutId: data.id!,
            teamId: data.teamId,
            data: {
              deviceInfo: {
                userAgent: navigator.userAgent,
              },
              customer,
              payments,
              items,
            },
          },
          eventId
        );

        const hasGtm = !!data.pixels?.find(
          (pixel) => pixel.type == 'google-tag-manager' && pixel.config.events.includes('initiateCheckout')
        );

        const hasFacebookPixel = !!data.pixels?.find(
          (pixel) => pixel.type === 'facebook' && pixel.config.events.includes('initiateCheckout')
        );

        const arrItems = items.map((item) => {
          return {
            description: item.description,
            code: item.code,
            amount: item.amount && item.amount > 0 ? item.amount / 100 : 0,
            quantity: item.quantity,
            type: item.type,
          };
        });

        const arrPayments = payments.map((payment) => {
          return {
            paymentMethod: payment.paymentMethod,
            value: payment.value && payment.value > 0 ? payment.value / 100 : 0,
          };
        });

        let amount = handleGetTotal();
        amount = amount && amount > 0 ? amount / 100 : 0;

        if (hasGtm) {
          window.dataLayer.push({
            event: 'InitiateCheckout',
            eventSourceUrl: window.location.href,
            checkoutId: data.id!,
            data: {
              amount: amount,
              deviceInfo: {
                userAgent: navigator.userAgent,
              },
              customer: customer,
              payments: arrPayments,
              items: arrItems,
            },
          });
        }

        if (hasFacebookPixel) {
          trackFacebookPixelEvent(
            'InitiateCheckout',
            {
              amount: amount,
              deviceInfo: {
                userAgent: navigator.userAgent,
              },
              customer: customer,
              payments: arrPayments,
              items: arrItems,
            },
            eventId
          );
        }
      }

      return checkoutService.pay(payload);
    },
    onSuccess: (data) => {
      if (!data) {
        return;
      }

      if (shouldGenerateTransactionFirst) {
        setTransaction(data);
        return;
      }

      if (['paid', 'waiting_payment', 'partially_paid'].includes(data.status)) {
        if (isSaveOCB && !ocbData && data.type !== 'quick-sell') {
          const ocb = buildOCBData(data);
          localStorage.setItem(`ocb-${data.teamId}`, ocb);
        }

        const url = modeAs == 'transaction' ? `/t/${data.id}/success` : `/c/${data.checkoutId}/success/${data.id}`;
        navigate(url, { state: { data } });
        return;
      }

      captureException(new Error('Error on pay'), { extra: { data } });
      setRefused(true);
      setRefusedError(new Error('Error on pay'));
    },
    onError: (err, variables) => {
      captureException(err, { extra: { data, variables } });
      console.error(err);
      setRefused(true);

      setRefusedError(err);
    },
  });

  const [form] = FormAntd.useForm();

  const customer = FormAntd.useWatch(['customer'], form);

  useEffect(() => {
    if (!isCached) {
      return;
    }

    const data = JSON.parse(decodeURIComponent(window.__data));
    setCachedData(data);
  }, [isCached]);

  useEffect(() => {
    if (modeAs == 'preview') {
      return;
    }

    if (!data?.pixels) {
      return;
    }

    const gtmPixel = data.pixels?.find((pixel) => pixel.type == 'google-tag-manager');

    if (gtmPixel) {
      if (!setupGoogleTagManager(gtmPixel.config.id!)) {
        data.pixels = data.pixels.filter((pixel) => pixel.type != 'google-tag-manager');
      }
    }

    const facebookPixel = data.pixels?.find((pixel) => pixel.type == 'facebook');
    if (facebookPixel) {
      setupFacebookPixel(facebookPixel.config.id!);
    }
  }, [data]);

  useEffect(() => {
    if (modeAs == 'preview') {
      return;
    }

    if (!data || !items.length) {
      return;
    }

    const eventId = v4();

    trackService.trackEvent(
      {
        eventName: 'PageView',
        eventTime: Date.now(),
        eventSourceUrl: window.location.href,
        checkoutId: data.id!,
        teamId: data.teamId,
        data: {
          deviceInfo: {
            userAgent: navigator.userAgent,
          },
          items: items,
        },
      },
      eventId
    );

    const hasGtm = !!data.pixels?.find(
      (pixel) => pixel.type == 'google-tag-manager' && pixel.config.events.includes('pageView')
    );

    if (hasGtm) {
      const arrItems = items.map((item) => {
        return {
          description: item.description,
          code: item.code,
          amount:
            item.unitValue && item.unitValue > 0
              ? item.unitValue / 100
              : item.amount && item.amount > 0
                ? item.amount / 100
                : 0,
          quantity: item.quantity,
          type: item.type,
        };
      });

      window.dataLayer.push({
        event: 'PageView',
        eventSourceUrl: window.location.href,
        checkoutId: data.id!,
        data: {
          deviceInfo: {
            userAgent: navigator.userAgent,
          },
          items: arrItems,
        },
      });
    }

    const hasFacebookPixel = !!data.pixels?.find(
      (pixel) => pixel.type == 'facebook' && pixel.config.events.includes('pageView')
    );

    if (hasFacebookPixel) {
      console.log('pageView facebook pixel');

      trackFacebookPixelEvent(
        'PageView',
        {
          deviceInfo: {
            userAgent: navigator.userAgent,
          },
          items: items,
        },
        eventId
      );
    }
  }, [data, items]);

  useEffect(() => {
    if (modeAs == 'preview') {
      return;
    }

    if (!customer || !data) {
      return;
    }

    const execute = async () => {
      if (!data) {
        return;
      }

      const formattedCustomer = buildCustomer({ customer: customer }, isForeigner);

      const eventId = v4();

      trackService.trackEvent(
        {
          eventName: 'Lead',
          eventTime: Date.now(),
          eventSourceUrl: window.location.href,
          checkoutId: data.id!,
          teamId: data.teamId,
          data: {
            deviceInfo: {
              userAgent: navigator.userAgent,
            },
            customer: formattedCustomer,
            items: items,
          },
        },
        eventId
      );

      const hasGtm = !!data.pixels?.find(
        (pixel) => pixel.type == 'google-tag-manager' && pixel.config.events.includes('lead')
      );

      const hasFacebookPixel = !!data.pixels?.find(
        (pixel) => pixel.type == 'facebook' && pixel.config.events.includes('lead')
      );

      const arrItems = items.map((item) => {
        return {
          description: item.description,
          code: item.code,
          amount:
            item.unitValue && item.unitValue > 0
              ? item.unitValue / 100
              : item.amount && item.amount > 0
                ? item.amount / 100
                : 0,
          quantity: item.quantity,
          type: item.type,
        };
      });

      if (hasGtm) {
        window.dataLayer.push({
          event: 'Lead',
          eventSourceUrl: window.location.href,
          checkoutId: data.id!,
          data: {
            deviceInfo: {
              userAgent: navigator.userAgent,
            },
            customer: customer,
            items: arrItems,
          },
        });
      }

      if (hasFacebookPixel) {
        trackFacebookPixelEvent(
          'Lead',
          {
            deviceInfo: {
              userAgent: navigator.userAgent,
            },
            customer: customer,
            items: arrItems,
          },
          eventId
        );
      }
    };

    const timeout = setTimeout(() => {
      execute();
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [customer, data, items]);

  const onFinish = useCallback(
    async (body: any) => {
      let customer = body.customer;

      if (!customer?.id && ocbData) {
        customer = { ...customer, ...ocbData?.customer };
      }

      mutate({ ...body, customer });
    },
    [ocbData, mutate]
  );

  const forceSubmit = useCallback(() => {
    setRefusedError(null);
    setShouldIgnoreIdempotency(true);
    form.submit();
  }, [form]);

  const handleAddItems = useCallback(
    (items: ICheckout['items']) => {
      setItems((prev) => {
        const newItems = [...prev];
        items.forEach((item) => {
          const index = newItems.findIndex((i) => i.id === item.id);
          if (index > -1) {
            newItems[index] = item;
            return;
          }
          newItems.push(item);
        });
        return newItems;
      });
    },
    [setItems]
  );

  const handleRemoveItems = useCallback((items: ICheckout['items']) => {
    setItems((prev) => {
      return prev.filter((item) => !items.find((i) => i.id === item.id));
    });
  }, []);

  const handleUpdateItemAmount = useCallback((itemId: string, amount: number) => {
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, amount } : item))
    );
  }, []);

  const handleGetTotal = useCallback(
    (withoutBump?: boolean) => {
      const hasEnrollmentFee = items.some((item) => item.type == 'enrollment_fee');
      const validItems = items.filter(
        (item) => !hasEnrollmentFee || ['enrollment_fee', 'order_bump'].includes(item.type)
      );

      return validItems.reduce((acc, item) => {
        if (withoutBump && item.type == 'order_bump') {
          return acc;
        }

        return acc + (item?.couponItem?.amount ?? item.unitValue ?? item.amount) * item.quantity;
      }, 0);
    },
    [items]
  );

  useEffect(() => {
    if (!data) {
      return;
    }

    const newItems = data.items.filter((item) =>
      ['main', 'cart', 'enrollment_fee', 'tax_fee', 'late_fee', 'daily_interest_fee'].includes(item.type)
    );

    const firstItems = newItems.filter((item) => ['main', 'cart'].includes(item.type));

    const firstItem = firstItems[0];
    if (firstItem) {
      document.title = `${firstItem.description} - Checkout`;
    }

    const isOpenValue = firstItem?.config?.isOpenValue;
    const itemsToSet = isOpenValue
      ? newItems.map((item) => (item.id === firstItem?.id ? { ...item, amount: 0 } : item))
      : newItems;

    if (data.companyFavicon) {
      const faviconUrl = resolveAssetUrl(data.companyFavicon);
      if (faviconUrl) {
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = faviconUrl;
      }
    }

    setItems(itemsToSet);

    if (data.styleConfig) {
      setStyle(data.styleConfig);
    }

    setPaymentMethods([
      ...(data.allowCreditCard ? ['credit_card'] : []),
      ...(data.allowPix ? ['pix'] : []),
      ...(data.allowBoleto ? ['boleto'] : []),
    ]);

    if (isOpenValue) {
      setInstallments([]);
    } else {
      setInstallments(data.installments ?? []);
    }

    const ocbData = localStorage.getItem(`ocb-${data.teamId}`);

    if (ocbData && data.type !== 'quick-sell') {
      setOcbData(JSON.parse(atob(ocbData)));
    }
  }, [data]);

  useEffect(() => {
    if (!data?.id || !data?.teamId || modeAs == 'transaction') {
      return;
    }

    socketService.connect({
      checkoutId: data.id!,
      teamId: data.teamId,
    });
  }, [data?.id, data?.teamId]);

  const firstPaymentMethod = paymentMethods[0];

  useEffect(() => {
    if (!firstPaymentMethod) {
      return;
    }

    const total = handleGetTotal() / 100.0;
    const firstItemForPayments = items.filter((item) => ['main', 'cart'].includes(item.type))[0];
    const isOpenValue = firstItemForPayments?.config?.isOpenValue;

    if (!total && !isOpenValue) {
      return;
    }

    const currentPayments = form.getFieldValue('payments') as any[] | undefined;

    if (currentPayments?.length) {
      const newValue = total > 0 ? moneyMask.apply(total / currentPayments.length) : '';
      const updatedPayments = currentPayments.map((payment: any) => ({
        ...payment,
        value: newValue,
      }));
      form.setFieldValue('payments', updatedPayments);
    } else {
      form.setFieldValue('payments', [
        {
          paymentMethod: firstPaymentMethod,
          value: total > 0 ? moneyMask.apply(total) : '',
        },
      ]);
    }
  }, [firstPaymentMethod, items]);

  if (isError && axios.isAxiosError(error)) {
    const code = error.response?.data?.code;
    const message = error.response?.data?.message;

    if (code == 'PAID') {
      return <ErrorPage title="Pedido já foi pago" renderLogo></ErrorPage>;
    }

    if (code == 'CANCELED') {
      return <ErrorPage title="Pedido cancelado" renderLogo></ErrorPage>;
    }

    if (code == 'NOT_FOUND') {
      return (
        <ErrorPage title="Checkout não encontrado" renderLogo>
          <p>Geralmente isso ocorre quando o link do checkout está incorreto.</p>
          <br />
          {data?.companyEmail && data?.companyPhone && (
            <p>
              Para mais informações entre em contato com{' '}
              <strong className="font-medium text-black">{data?.companyEmail}</strong> ou no telefone{' '}
              <strong className="font-medium text-black">{formatPhone(data?.companyPhone)}</strong>
            </p>
          )}
        </ErrorPage>
      );
    }

    return (
      <ErrorPage title="Erro ao carregar checkout" renderLogo>
        {message || 'Tente novamente mais tarde...'}
      </ErrorPage>
    );
  }

  if (isLoading || !data) {
    return <Spin tip="Carregando..." fullscreen size="large"></Spin>;
  }

  if (!data.isActive && data.inactiveRedirectUrl && modeAs !== 'preview') {
    window.location.href = data.inactiveRedirectUrl;
    return <Spin tip="Redirecionando..." fullscreen size="large"></Spin>;
  }

  if (data && (!data.isActive || !data.hasActiveGateways) && modeAs != 'preview') {
    return (
      <ErrorPage title="Checkout indisponível" renderLogo>
        <p>Geralmente isso ocorre quando o checkout foi desabilitado ou está fora do período de vendas.</p>
        <br />
        {data?.companyEmail && data?.companyPhone && (
          <p>
            Para mais informações entre em contato com{' '}
            <strong className="font-medium text-black">{data?.companyEmail}</strong> ou no telefone{' '}
            <strong className="font-medium text-black">{formatPhone(data?.companyPhone)}</strong>
          </p>
        )}
      </ErrorPage>
    );
  }

  const firstItems = items.filter((item) => ['main', 'cart'].includes(item.type));
  const firstItem = firstItems[0];
  const hasEnrollmentFee = items.some((item) => item.type == 'enrollment_fee');
  let isRecurrence = firstItem?.billingType === 'recurrence';
  if (isRecurrence && firstItem?.recurrenceChargesQuantity === 1 && !hasEnrollmentFee) {
    isRecurrence = false;
  }

  if (!firstItem) {
    return <Spin tip="Carregando..." fullscreen size="large"></Spin>;
  }

  const hasSomePaymentMethod = paymentMethods.length > 0;

  const buildInitialValues = () => {
    const totalValue = handleGetTotal() / 100.0;
    const firstItemForOpen = items.filter((item) => ['main', 'cart'].includes(item.type))[0];
    const isOpenValue = firstItemForOpen?.config?.isOpenValue;
    const paymentValue = isOpenValue ? '' : moneyMask.apply(totalValue);

    const hasOcb = !!localStorage.getItem(`ocb-${data!.teamId}`);
    if (hasOcb && !['preview', 'transaction'].includes(modeAs) && data?.type !== 'quick-sell') {
      const ocb = JSON.parse(atob(localStorage.getItem(`ocb-${data!.teamId}`)!));
      const validPayments = ocb.payments.filter((payment: any) => paymentMethods.includes(payment.paymentMethod));

      const totalPerPayment = totalValue / (validPayments.length ?? 1);

      const result = {
        customer: {
          id: ocb.customer.id,
          name: ocb.customer.name,
        },
        payments: validPayments.map((payment: any) => {
          return {
            paymentMethod: 'credit_card',
            value: Number(totalPerPayment.toFixed(2)),
            creditCard: {
              id: payment.id,
            },
          };
        }),
        ...(data?.askFinancialResponsible
          ? {
              financialResponsible: {
                ddi: 'BR55',
                country: 'BR',
              },
            }
          : {}),
      };

      if (result.payments.length == 0) {
        result.payments = [
          {
            paymentMethod: firstPaymentMethod,
            value: paymentValue,
          },
        ];
      }

      return result;
    }

    let customer = {
      name: searchParams?.get('name') ?? searchParams?.get('nome') ?? '',
      email: searchParams?.get('email') ?? '',
      document: searchParams?.get('document') ?? searchParams?.get('documento') ?? searchParams?.get('doc') ?? '',
      phone: searchParams?.get('phone') ?? searchParams?.get('telefone') ?? searchParams?.get('cel') ?? '',
      ddi: 'BR55',
      country: 'BR',
      ...(data?.askFinancialResponsible
        ? {
            financialResponsible: {
              ddi: 'BR55',
              country: 'BR',
            },
          }
        : {}),
    } as any;

    if (modeAs == 'transaction' && data?.customer) {
      if (data?.type === 'quick-sell') {
        const documentValue = data.customer.document || '';
        const phoneValue = data.customer.phone || '';

        customer = {
          id: data.customer.id,
          name: data.customer.name,
          email: data.customer.email || '',
          document: documentValue ? documentMask.apply(documentValue) : '',
          phone: phoneValue ? phoneMask.apply(phoneValue) : '',
          ddi: 'BR55',
          country: 'BR',
        } as any;
      } else {
        customer = {
          id: data.customer.id,
          name: data.customer.name,
        } as any;
      }
    }

    return {
      customer,
      payments: [
        {
          paymentMethod: firstPaymentMethod,
          value: paymentValue,
        },
      ],
    };
  };

  const handleDisableOCB = () => {
    localStorage.removeItem(`ocb-${data!.teamId}`);
    setOcbData(undefined);

    form.setFieldsValue(buildInitialValues());
  };

  const handleSetForeigner = (enabled: boolean) => {
    form.setFieldsValue({
      customer: {
        ...form.getFieldValue('customer'),
        document: undefined,
        address: undefined,
      },
    });

    form.setFieldsValue({
      payments: [
        {
          paymentMethod: firstPaymentMethod,
          value: moneyMask.apply(handleGetTotal() / 100.0),
        },
      ],
    });

    if (enabled) {
      setPaymentMethods(['credit_card']);
    }

    if (!enabled) {
      setPaymentMethods([
        ...(data?.allowCreditCard ? ['credit_card'] : []),
        ...(data?.allowPix ? ['pix'] : []),
        ...(data?.allowBoleto ? ['boleto'] : []),
      ]);
    }

    setIsForeigner(enabled);
  };

  const inResumeMode = isOCB || (modeAs == 'transaction' && data?.customer?.id && data?.type !== 'quick-sell');
  const canChangeForeigner = !isOCB && !data?.customer?.id && paymentMethods.includes('credit_card');

  const hasBackgroundColor = data?.styleConfig?.backgroundColor;
  let hasBackgroundImage = data?.styleConfig?.backgroundImage;
  const hasRightBanner = data?.styleConfig?.rightBanner;
  const hasDetails = firstItem?.details;

  if (Array.isArray(hasBackgroundImage) || typeof hasBackgroundImage !== 'string') {
    hasBackgroundImage = undefined;
  }

  return (
    <div
      className={twMerge('py-4 px-4', hasBackgroundImage && 'bg-cover bg-center bg-no-repeat')}
      style={{
        backgroundColor: hasBackgroundColor,
        backgroundImage: hasBackgroundImage && `url(${resolveAssetUrl(hasBackgroundImage)})`,
      }}
    >
      <FormProvider
        value={{
          checkout: data!,
          form,
          isPaying: isPending,
          items,
          modeAs,
          addItems: handleAddItems,
          removeItems: handleRemoveItems,
          getTotal: handleGetTotal,
          isCached,
          isOCB,
          ocbData,
          disableOCB: handleDisableOCB,
          isRecurrence,
          firstItem,
          isForeigner,
          setForeignerMode: handleSetForeigner,
          isFinancialResponsibleForeigner,
          setFinancialResponsibleForeignerMode: setIsFinancialResponsibleForeigner,
          canChangeForeigner,
          paymentMethods,
          setPaymentMethods,
          isRefused,
          setRefused,
          showTerms,
          setShowTerms,
          hasError: isError || isRefused,
          error: error ?? refusedError,
          forceSubmit,
          transaction,
          setCanSignDocument,
          canSignDocument,
          isDocumentSigned,
          setDocumentSigned,
          appliedCoupon,
          setAppliedCoupon,
          installments,
          setInstallments,
          updateItemAmount: handleUpdateItemAmount,
        }}
      >
        <main
          className={twMerge(
            'w-full flex flex-col items-center justify-center gap-4 mx-auto',
            !hasRightBanner && !hasDetails ? 'max-w-2xl' : 'max-w-5xl'
          )}
        >
          {data?.isTest && modeAs != 'preview' && <AlertHomologMode />}

          <PayingModal />
          <RefusedModal />
          <IdempotencyModal />
          <TermsModal />

          {canSignDocument && <ExternalDocumentModal />}

          <BannerTop />

          {data.type == 'transaction' && data.status == 'partially_paid' && (
            <CardDefault className="bg-orange-50 border-orange-200">
              <div className="space-y-4">
                <div className="text-lg font-medium text-orange-800 flex items-center">
                  <ExclamationCircleOutlined style={{ fontSize: 20, marginRight: 8, color: '#ea580c' }} />
                  Pagamento Parcial
                </div>

                <div className="space-y-2">
                  <Text className="text-base text-orange-700">
                    Seu pagamento foi processado parcialmente. Para concluir sua compra, é necessário efetuar o
                    pagamento do valor restante.
                  </Text>
                </div>
              </div>
            </CardDefault>
          )}

          <div className="flex flex-col sm:flex-row w-full justify-center gap-4">
            <div className="w-full max-w-2xl flex flex-col gap-4">
              <CardDefault className="bg-white p-4 sm:p-8">
                <Header />

                <FormAntd
                  className={className}
                  form={form}
                  scrollToFirstError
                  layout="vertical"
                  initialValues={buildInitialValues()}
                  onFinish={onFinish}
                >
                  {!inResumeMode && <BasicInfo name={['customer']} isForeigner={isForeigner} onChangeForeigner={handleSetForeigner} />}

                  {inResumeMode && <BasicInfoResumed name={['customer']} />}

                  {data?.askAddress && !inResumeMode && <Address name={['customer', 'address']} isForeigner={isForeigner} />}

                  <FinancialResponsible />

                  {data?.allowAddPayment && hasSomePaymentMethod && <Payment />}

                  <OrderBump />

                  <div className="mt-6 space-y-4">
                    {data?.type !== 'quick-sell' && (
                      <Checkbox checked={isSaveOCB} onChange={onChangeSaveOCB}>
                        Usar esses dados nas próximas compras
                      </Checkbox>
                    )}

                    <Checkbox onChange={onChange}>
                      Concordo que li, e compreendi os
                      <span className="ml-1 cursor-pointer text-blue-500" onClick={() => setShowTerms(true)}>
                        Termos de Compra
                      </span>
                    </Checkbox>
                  </div>

                  {data?.allowAddPayment && hasSomePaymentMethod && (
                    <div className="mt-4">
                      <Button
                        htmlType="submit"
                        type="primary"
                        block
                        size="large"
                        disabled={isPending || !enablePay}
                        loading={isPending}
                      >
                        Comprar Agora
                      </Button>
                    </div>
                  )}
                </FormAntd>

                <Footer />
              </CardDefault>

              <BannerBottom />
            </div>

            <BannerRight />

            {hasDetails && !hasRightBanner && (
              <div className="w-100 details-container hidden sm:block">
                <div className="font-medium text-base mb-2 text-black tracking-wide">Detalhes</div>
                <span
                  className="text-[15px] leading-[20px] text-gray-600"
                  dangerouslySetInnerHTML={{ __html: hasDetails }}
                />
                <Owner />
              </div>
            )}
          </div>
        </main>
      </FormProvider>
    </div>
  );
});

export default Form;
